import { Text, Box, Flex, Table, Tbody, Td, Th, Thead, Tr, Checkbox, Icon, UseDisclosureReturn, useDisclosure } from '@chakra-ui/react';
import {
	useReactTable,
	SortingState,
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
} from "@tanstack/react-table";

import Card from 'components/card/Card';

import { AxiosResponse } from 'axios';
import { MdModeEdit } from "react-icons/md";

import { useState } from 'react';
import { DynamicModalFormOptions } from 'components/managementModals/DynamicFormModal';

export type ManagementModalProps = {
	mode: 'update' | 'create',
	object?: any,
	options?: DynamicModalFormOptions[],
	disclosure?: UseDisclosureReturn,
	viewReloader?: (key: number) => void
}

export const TextValue = (props: { val: string | any }) => (
	<Flex align='center'>
		<Text color={'white'} fontSize='sm' fontWeight='700'>
			{props.val}
		</Text>
	</Flex>
)

export const CheckValue = (props: {
	val: any,
	methodCheck: (id: number) => Promise<AxiosResponse<any, any>>,
	methodUncheck: (id: number) => Promise<AxiosResponse<any, any>>,
	reloader: (key: number) => void
}) => {

	const { val, methodCheck, methodUncheck, reloader } = props
	const [checked, setChecked] = useState(val.is_active)
	const setActive = async () => {

		if (checked) {
			await methodUncheck(val.id).then(() => setChecked(false))
		} else {
			await methodCheck(val.id).then(() => setChecked(true))
		}
		reloader(Math.random())
	}

	return (
		<Flex justifyContent='center'>
			<Checkbox defaultChecked={checked} colorScheme='brandScheme' me='10px' onChange={() => { setActive() }} />
		</Flex>
	)
}

export const EditModal = (props: { modalComponent: any, modalProps: ManagementModalProps, disabled: boolean }) => {

	const { modalComponent, modalProps, disabled } = props
	const iconProps = disabled === true ? { color: 'gray.300' } : { color: 'yellow.300', _hover: { color: 'yellow.500' }, onClick: () => disclosure.onOpen() }
	const disclosure = useDisclosure()

	const Modal = modalComponent
	modalProps.disclosure = disclosure

	return (
		<>
			<Flex justifyContent='center'>
				<Icon as={MdModeEdit} {...iconProps} />
			</Flex>
			<Modal {...modalProps} />
		</>
	)
}

export default function ManagementTable(props: { data: any[], columns: any[] }) {

	const { data, columns } = props;
	const [sorting, setSorting] = useState<SortingState>([]);
	const [screenH, setScreenH] = useState(window.screen.availHeight)

	const borderColor = 'whiteAlpha.100';

	const table = useReactTable({
		data,
		columns,
		state: {
			sorting
		},
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		debugTable: true
	});
	return (
		<Card p={"5px"} pb={"10px"}>
			<Card flexDirection='column' w='100%' px='0px' overflowX='auto' pt={'0px'} h={{ base: screenH - 650, xl: screenH - 300 }}>
				<Box>
					<Table variant='simple' color='gray.500'>
						<Thead position="sticky" top="0px" zIndex={100}>
							{table.getHeaderGroups().map((headerGroup) => (
								<Tr key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										return (
											<Th
												key={header.id}
												colSpan={header.colSpan}
												pe='10px'
												bgColor="#111c44"
												borderColor={borderColor}
												cursor='pointer'
												paddingInlineStart='0px'
												onClick={header.column.getToggleSortingHandler()}>
												<Flex
													justifyContent='space-between'
													align='center'
													fontSize={{ sm: '10px', lg: '12px' }}
													color='gray.400'>
													{flexRender(header.column.columnDef.header, header.getContext())}{{
														asc: '',
														desc: '',
													}[header.column.getIsSorted() as string] ?? null}
												</Flex>
											</Th>
										);
									})}
								</Tr>
							))}
						</Thead>
						<Tbody>
							{table.getRowModel().rows.map((row) => {
								return (
									<Tr key={row.id}>
										{row.getVisibleCells().map((cell) => {
											return (
												<Td
													key={cell.id}
													fontSize={{ sm: '14px' }}
													minW={{ sm: '150px', md: '200px', lg: 'auto' }}
													borderColor='transparent'>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</Td>
											);
										})}
									</Tr>
								);
							})}
						</Tbody>
					</Table>
				</Box>
			</Card>
		</Card>
	);
}